import { configureStore, createSlice } from "@reduxjs/toolkit";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { consultFormReducer } from "../consult-form.jsx";

const STORAGE_KEY = "squra:form-drafts:v1";
const MAX_DRAFTS = 50;
const MAX_AGE = 14 * 24 * 60 * 60 * 1000;
const SENSITIVE_PATTERN =
  /password|passwd|pwd|token|secret|api.?key|authorization|otp|captcha|cvv|credit.?card/i;
const IGNORED_TYPES = new Set([
  "button",
  "file",
  "hidden",
  "image",
  "password",
  "reset",
  "submit",
]);

function loadDrafts() {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(parsed)
        .filter(
          ([, draft]) => draft?.updatedAt && now - draft.updatedAt < MAX_AGE,
        )
        .slice(-MAX_DRAFTS),
    );
  } catch {
    return {};
  }
}

const draftsSlice = createSlice({
  name: "formDrafts",
  initialState: { entries: loadDrafts(), lastSavedAt: 0 },
  reducers: {
    saveDraft(state, action) {
      const { key, values } = action.payload;
      state.entries[key] = { values, updatedAt: Date.now() };
      const keys = Object.keys(state.entries).sort(
        (left, right) =>
          state.entries[left].updatedAt - state.entries[right].updatedAt,
      );
      keys.slice(0, Math.max(0, keys.length - MAX_DRAFTS)).forEach((entry) => {
        delete state.entries[entry];
      });
      state.lastSavedAt = Date.now();
    },
    removeDraft(state, action) {
      delete state.entries[action.payload];
    },
    removeDrafts(state, action) {
      action.payload.forEach((key) => delete state.entries[key]);
    },
  },
});

export const formDraftActions = draftsSlice.actions;

export const formDraftStore = configureStore({
  reducer: { consultForm: consultFormReducer, formDrafts: draftsSlice.reducer },
});

let persistTimer;
formDraftStore.subscribe(() => {
  window.clearTimeout(persistTimer);
  persistTimer = window.setTimeout(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(formDraftStore.getState().formDrafts.entries),
      );
    } catch {
      // Storage can be unavailable in private or restricted browser contexts.
    }
  }, 180);
});

function fieldIdentity(field, index) {
  const base = field.name || field.id || `field-${index}`;
  return field.type === "checkbox" || field.type === "radio"
    ? `${base}:${field.value || index}`
    : base;
}

function isPersistable(field) {
  if (
    !(field instanceof HTMLElement) ||
    field.matches("[data-no-draft]") ||
    field.closest("form[data-no-draft]")
  )
    return false;
  const type = String(field.type || "").toLowerCase();
  const identity = `${field.name || ""} ${field.id || ""} ${field.autocomplete || ""}`;
  return (
    !field.disabled &&
    !IGNORED_TYPES.has(type) &&
    !SENSITIVE_PATTERN.test(identity) &&
    field.matches("input, select, textarea")
  );
}

function formKey(scope, form, index) {
  const action = form.getAttribute("action") || "";
  const fieldSignature = [...form.elements]
    .slice(0, 3)
    .map((field, fieldIndex) => fieldIdentity(field, fieldIndex))
    .join("-");
  const identity =
    form.id ||
    form.getAttribute("name") ||
    action ||
    fieldSignature ||
    `form-${index}`;
  return `${scope}:${window.location.pathname}:${identity}`;
}

function serializeForm(form) {
  const values = {};
  [...form.elements].forEach((field, index) => {
    if (!isPersistable(field)) return;
    const key = fieldIdentity(field, index);
    if (field.type === "checkbox" || field.type === "radio") {
      values[key] = {
        kind: field.type,
        value: field.value,
        checked: field.checked,
      };
    } else if (field instanceof HTMLSelectElement && field.multiple) {
      values[key] = {
        kind: "multiple",
        value: [...field.selectedOptions].map((option) => option.value),
      };
    } else {
      values[key] = { kind: "value", value: field.value };
    }
  });
  return values;
}

function hasDraftContent(values) {
  return Object.values(values).some((entry) => {
    if (entry.kind === "checkbox" || entry.kind === "radio")
      return entry.checked;
    if (entry.kind === "multiple") return entry.value.length > 0;
    return String(entry.value ?? "").trim().length > 0;
  });
}

function setFieldValue(field, value) {
  if (value.kind === "checkbox" || value.kind === "radio") {
    field.checked = Boolean(value.checked);
  } else if (value.kind === "multiple" && field instanceof HTMLSelectElement) {
    [...field.options].forEach((option) => {
      option.selected = value.value.includes(option.value);
    });
  } else {
    let prototype = HTMLInputElement.prototype;
    if (field instanceof HTMLTextAreaElement) {
      prototype = HTMLTextAreaElement.prototype;
    } else if (field instanceof HTMLSelectElement) {
      prototype = HTMLSelectElement.prototype;
    }
    const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;
    if (setter) setter.call(field, value.value ?? "");
    else field.value = value.value ?? "";
  }
  field.dispatchEvent(new Event("input", { bubbles: true }));
  field.dispatchEvent(new Event("change", { bubbles: true }));
}

function restoreForm(form, draft) {
  if (!draft?.values || form.dataset.squraDraftReady === "true") return;
  [...form.elements].forEach((field, index) => {
    if (!isPersistable(field)) return;
    const saved = draft.values[fieldIdentity(field, index)];
    if (saved) setFieldValue(field, saved);
  });
  form.dataset.squraDraftReady = "true";
}

export function FormDraftPersistence({ scope }) {
  const dispatch = useDispatch();
  const timers = useRef(new WeakMap());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let badgeTimer;
    const forms = () => [...document.querySelectorAll("form")];
    const identify = (form) => formKey(scope, form, forms().indexOf(form));
    const enhance = () => {
      const entries = formDraftStore.getState().formDrafts.entries;
      forms().forEach((form, index) => {
        restoreForm(form, entries[formKey(scope, form, index)]);
      });
    };
    const showSavedBadge = () => {
      setSaved(true);
      window.clearTimeout(badgeTimer);
      badgeTimer = window.setTimeout(setSaved.bind(null, false), 1400);
    };
    const save = (form) => {
      const currentTimer = timers.current.get(form);
      window.clearTimeout(currentTimer);
      timers.current.set(
        form,
        window.setTimeout(() => {
          const key = identify(form);
          const values = serializeForm(form);
          dispatch(
            hasDraftContent(values)
              ? draftsSlice.actions.saveDraft({ key, values })
              : draftsSlice.actions.removeDraft(key),
          );
          showSavedBadge();
        }, 220),
      );
    };
    const onInput = (event) => {
      const form = event.target.closest?.("form");
      if (form && isPersistable(event.target)) save(form);
    };
    const onReset = (event) => {
      const form = event.target.closest?.("form");
      if (form) dispatch(draftsSlice.actions.removeDraft(identify(form)));
    };
    const clearWithin = (event) => {
      const root = event.detail?.root
        ? document.querySelector(event.detail.root)
        : document;
      if (!root) return;
      const keys = [...root.querySelectorAll("form")].map(identify);
      if (keys.length) dispatch(draftsSlice.actions.removeDrafts(keys));
    };
    enhance();
    const observer = new MutationObserver(enhance);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener("input", onInput, true);
    document.addEventListener("change", onInput, true);
    document.addEventListener("reset", onReset, true);
    document.addEventListener("squra:form-saved", clearWithin);
    return () => {
      observer.disconnect();
      window.clearTimeout(badgeTimer);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("change", onInput, true);
      document.removeEventListener("reset", onReset, true);
      document.removeEventListener("squra:form-saved", clearWithin);
    };
  }, [dispatch, scope]);

  return (
    <span
      className={`sq-draft-toast${saved ? " is-visible" : ""}`}
      role="status"
      aria-atomic="true"
      aria-live="polite"
    >
      <span aria-hidden="true">✓</span>
      {" Draft tersimpan"}
    </span>
  );
}
